import { apiFetch } from '@/lib/api-client';
import type { AuthTokens } from '@/lib/auth-types';

/**
 * API client for Batch 9B Commit 0's POST /api/auth/change-password
 * endpoint.
 *
 * Backend behaviour:
 *   1. Verifies `currentPassword` via bcrypt.compare → 401 on mismatch
 *   2. Hashes + persists `newPassword`
 *   3. Revokes ALL existing refresh tokens (force re-login on every
 *      device)
 *   4. Issues a fresh `TokenPair` so the requesting session keeps a
 *      valid refresh token
 *
 * PWA pattern: call → replace stored tokens via
 * `authStore.replaceTokens(pair)` → render success modal → navigate
 * to /account on close.
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export function changePassword(payload: ChangePasswordRequest): Promise<AuthTokens> {
  return apiFetch<AuthTokens>('/api/auth/change-password', {
    method: 'POST',
    body: payload,
  });
}

/**
 * Account-Delete (A-15, Batch 9C). Calls
 * `DELETE /api/users/me` with optional reason + feedback fields
 * (both DISCARDED server-side at Mai-Launch — analytics persistence
 * deferred to AccountDeletionReason tracker entry).
 *
 * On success the backend returns `{ message: 'Account deleted' }`.
 * Caller is responsible for clearing all PWA-side caches +
 * navigating to /auth/login.
 *
 * Reason enum values mirror Briefing line 572 verbatim and the
 * backend's `ACCOUNT_DELETE_REASONS` constant.
 */
export type AccountDeleteReason =
  | 'too_little_use'
  | 'too_expensive'
  | 'alternatives_better'
  | 'privacy_concerns'
  | 'other';

export interface DeleteAccountRequest {
  reason?: AccountDeleteReason;
  feedback?: string;
}

export function deleteAccount(payload: DeleteAccountRequest = {}): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/api/users/me', {
    method: 'DELETE',
    body: payload,
  });
}
